import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Block as BlockIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace(".", ",")}`;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    {
      key: "total_companies",
      title: "Total de Empresas",
      value: stats?.total_companies || 0,
      icon: <BusinessIcon sx={{ fontSize: 32 }} />,
      color: "#0ea5e9",
      bgColor: "#e0f2fe",
    },
    {
      key: "active_companies",
      title: "Empresas Ativas",
      value: stats?.active_companies || 0,
      icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
      color: "#10b981",
      bgColor: "#d1fae5",
    },
    {
      key: "companies_in_trial",
      title: "Período de Teste",
      value: stats?.companies_in_trial || 0,
      icon: <HourglassIcon sx={{ fontSize: 32 }} />,
      color: "#f59e0b",
      bgColor: "#fef3c7",
    },
    {
      key: "companies_paid",
      title: "Pagas",
      value: stats?.companies_paid || 0,
      icon: <CheckCircleIcon sx={{ fontSize: 32 }} />,
      color: "#10b981",
      bgColor: "#d1fae5",
    },
    {
      key: "companies_overdue",
      title: "Em Atraso",
      value: stats?.companies_overdue || 0,
      icon: <BlockIcon sx={{ fontSize: 32 }} />,
      color: "#f59e0b",
      bgColor: "#fef3c7",
    },
    {
      key: "companies_blocked",
      title: "Bloqueadas",
      value: stats?.companies_blocked || 0,
      icon: <BlockIcon sx={{ fontSize: 32 }} />,
      color: "#ef4444",
      bgColor: "#fee2e2",
    },
    {
      key: "total_users",
      title: "Total de Usuários",
      value: stats?.total_users || 0,
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      color: "#8b5cf6",
      bgColor: "#ede9fe",
    },
    {
      key: "monthly_revenue",
      title: "Receita Mensal",
      value: formatMoney(stats?.monthly_revenue || 0),
      icon: <MoneyIcon sx={{ fontSize: 32 }} />,
      color: "#10b981",
      bgColor: "#d1fae5",
      isMoney: true,
    },
  ];

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Painel Administrativo
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gerencie todas as empresas do sistema
        </Typography>
      </Box>

      {/* Cards – 2 linhas com 4 colunas */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: 3,
        }}
      >
        {statCards.map((card) => (
          <Card
            key={card.key}
            sx={{
              height: "100%",
              borderRadius: 2,
              boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: card.bgColor,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    noWrap
                  >
                    {card.title}
                  </Typography>

                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{
                      fontSize: { xs: "1.8rem", md: "2.1rem" },
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {card.value}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Container>
  );
}
